import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useActiveBling } from "@/lib/blingContext";
import { toast } from "sonner";
import {
  Calculator, Search, AlertTriangle, TrendingUp, Loader2, X, Settings, Plus, Trash2,
  DollarSign, Percent, Tag, Target, Package,
} from "lucide-react";

const brl = (v) => (Number(v || 0)).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const pct = (v) => `${Number(v || 0).toFixed(2).replace(".", ",")}%`;

// -----------------------------------------------------------------
// Persistent input hook — keeps calculator values in localStorage.
// Values are preserved across page reloads. Users clear them by
// blanking the field or overwriting them — the requirement was:
// "o que for preenchido se mantém na precificação a não ser que
// seja deixado em branco ou alterado".
// -----------------------------------------------------------------
const LS_KEY = "vyntric.pricing.v2";

function usePersistentState(key, initial) {
  const [value, setValue] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (!raw) return initial;
      const parsed = JSON.parse(raw);
      return key in parsed ? parsed[key] : initial;
    } catch {
      return initial;
    }
  });
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      parsed[key] = value;
      localStorage.setItem(LS_KEY, JSON.stringify(parsed));
    } catch {
      /* ignore quota errors */
    }
  }, [key, value]);
  return [value, setValue];
}

export default function Pricing() {
  // Channels — loaded from API (auto-seeded on first open)
  const [canais, setCanais] = useState([]);
  const [loadingCanais, setLoadingCanais] = useState(true);
  const [showConfig, setShowConfig] = useState(false);
  const [showOpCostCalc, setShowOpCostCalc] = useState(false);

  // Persisted inputs
  const [custoProduto, setCustoProduto] = usePersistentState("custoProduto", "");
  const [embalagem, setEmbalagem] = usePersistentState("embalagem", "");
  const [frete, setFrete] = usePersistentState("frete", "");
  const [canalKey, setCanalKey] = usePersistentState("canalKey", "loja");
  const [comissao, setComissao] = usePersistentState("comissao", 0);
  const [taxaFixa, setTaxaFixa] = usePersistentState("taxaFixa", 0);
  const [imposto, setImposto] = usePersistentState("imposto", "");
  const [custosFixos, setCustosFixos] = usePersistentState("custosFixos", "");
  const [marketing, setMarketing] = usePersistentState("marketing", "");
  const [margem, setMargem] = usePersistentState("margem", "20");
  const [precoTeste, setPrecoTeste] = usePersistentState("precoTeste", "");

  const loadCanais = async () => {
    setLoadingCanais(true);
    try {
      const { data } = await api.get("/pricing/channels");
      setCanais(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erro ao carregar canais");
    } finally {
      setLoadingCanais(false);
    }
  };

  useEffect(() => { loadCanais(); }, []);

  // Sync channel-derived fields (comissão, taxa fixa, imposto) when the
  // user picks a channel. Fields are treated as channel presets — they
  // override any prior manual edit. Everything else (custo, embalagem,
  // frete, custos fixos, marketing, margem) is persisted in localStorage
  // and never wiped by a channel change.
  const prevCanalKey = useRef(null);
  useEffect(() => {
    if (!canais.length) return;
    const c = canais.find((x) => x.key === canalKey) || canais[0];
    if (!c) return;
    // On first mount, keep whatever the user had saved. Only refill on
    // an explicit change.
    if (prevCanalKey.current !== null && prevCanalKey.current !== c.key) {
      setComissao(c.com);
      setTaxaFixa(c.taxa_fixa);
      setImposto(String(c.imposto ?? ""));
    }
    prevCanalKey.current = c.key;
    // Snap the dropdown to a valid key if the previous one was removed
    if (c.key !== canalKey) setCanalKey(c.key);
  }, [canalKey, canais]);

  const clearAll = () => {
    if (!confirm("Limpar todos os campos da precificação?")) return;
    setCustoProduto("");
    setEmbalagem("");
    setFrete("");
    setImposto("");
    setCustosFixos("");
    setMarketing("");
    setMargem("20");
    setPrecoTeste("");
    toast.success("Campos limpos");
  };

  const n = (v) => Number(String(v).replace(",", ".") || 0) || 0;
  const custoDireto = n(custoProduto) + n(embalagem) + n(frete) + n(taxaFixa);
  const somaPct = n(comissao) + n(imposto) + n(custosFixos) + n(marketing) + n(margem);
  const somaPctSemMargem = n(comissao) + n(imposto) + n(custosFixos) + n(marketing);
  const invalid = somaPct >= 100;
  const precoVenda = invalid ? 0 : custoDireto / (1 - somaPct / 100);
  const markup = custoDireto > 0 ? precoVenda / custoDireto : 0;
  const lucroLiquido = precoVenda * n(margem) / 100;
  const precoMinimo = invalid ? 0 : (somaPctSemMargem >= 100 ? 0 : custoDireto / (1 - somaPctSemMargem / 100));

  const breakdown = [
    { label: "Custo + embalagem + frete", value: n(custoProduto) + n(embalagem) + n(frete), color: "#64748b" },
    { label: "Taxa fixa do canal", value: n(taxaFixa), color: "#0ea5e9" },
    { label: "Comissão", value: precoVenda * n(comissao) / 100, color: "#3b82f6" },
    { label: "Impostos", value: precoVenda * n(imposto) / 100, color: "#f59e0b" },
    { label: "Custo de operação", value: precoVenda * n(custosFixos) / 100, color: "#a855f7" },
    { label: "Marketing", value: precoVenda * n(marketing) / 100, color: "#ec4899" },
    { label: "Lucro líquido", value: lucroLiquido, color: "#10b981" },
  ];

  // Test price
  const testeAtivo = precoTeste !== "" && n(precoTeste) > 0;
  const valorCustosPercent = testeAtivo ? n(precoTeste) * somaPctSemMargem / 100 : 0;
  const lucroTeste = testeAtivo ? n(precoTeste) - custoDireto - valorCustosPercent : 0;
  const margemTeste = testeAtivo && n(precoTeste) > 0 ? (lucroTeste / n(precoTeste)) * 100 : 0;

  return (
    <div className="space-y-5" data-testid="pricing-page">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
            <Calculator className="w-3 h-3" /> Ferramentas
          </div>
          <h1 className="text-3xl font-black tracking-tighter">Precificação</h1>
          <p className="text-sm text-slate-600 mt-1">
            Recalcula automaticamente. Os valores ficam salvos entre sessões.
          </p>
        </div>
        <button
          onClick={clearAll}
          data-testid="pricing-clear-btn"
          className="px-3 py-2 text-xs font-bold uppercase tracking-wider text-slate-600 hover:text-red-700 hover:bg-red-50 inline-flex items-center gap-1.5 rounded-sm border border-slate-200"
        >
          <Trash2 className="w-3.5 h-3.5" /> Limpar campos
        </button>
      </div>

      {/* Two columns: inputs (left) + sticky results (right) */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-5 items-start">
        {/* ==================== LEFT: INPUTS ==================== */}
        <div className="space-y-4">
          {/* Product picker */}
          <Section title="Produto" icon={<Package className="w-3 h-3" />}>
            <ProductAutocomplete
              onSelect={(p) => {
                // Preserve 0 (user might legitimately have a 0-cost item);
                // only leave the field blank when the value is truly absent.
                if (p.preco_custo === undefined || p.preco_custo === null) return;
                setCustoProduto(String(p.preco_custo));
              }}
            />
          </Section>

          {/* Direct costs — 3 columns on desktop */}
          <Section title="Custos diretos (R$)" icon={<DollarSign className="w-3 h-3" />}>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <NumField label="Custo do produto" value={custoProduto} onChange={setCustoProduto} testid="pricing-custo-produto" prefix="R$" />
              <NumField label="Embalagem / insumos" value={embalagem} onChange={setEmbalagem} testid="pricing-embalagem" prefix="R$" />
              <NumField label="Frete pago pelo vendedor" value={frete} onChange={setFrete} testid="pricing-frete" prefix="R$" />
            </div>
          </Section>

          {/* Channel + its 3 preset values */}
          <Section title="Canal de venda" icon={<Tag className="w-3 h-3" />}>
            <div className="flex gap-2 mb-3">
              <select
                data-testid="pricing-canal"
                value={canalKey}
                onChange={(e) => setCanalKey(e.target.value)}
                disabled={loadingCanais}
                className="flex-1 border border-slate-300 bg-white px-3 py-2.5 text-sm font-bold rounded-sm focus:outline-none focus:border-[#002EA6] disabled:bg-slate-50"
              >
                {loadingCanais ? (
                  <option>Carregando…</option>
                ) : (
                  canais.map((c) => (
                    <option key={c.key} value={c.key}>{c.label}</option>
                  ))
                )}
              </select>
              <button
                data-testid="pricing-canais-config-btn"
                type="button"
                onClick={() => setShowConfig(true)}
                title="Configurar canais de venda"
                className="px-3 py-2.5 border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5 rounded-sm"
              >
                <Settings className="w-3.5 h-3.5" /> Configurar
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <NumField label="Comissão" value={comissao} onChange={setComissao} testid="pricing-comissao" suffix="%" />
              <NumField label="Taxa fixa" value={taxaFixa} onChange={setTaxaFixa} testid="pricing-taxa-fixa" prefix="R$" />
              <NumField label="Imposto" value={imposto} onChange={setImposto} testid="pricing-imposto" suffix="%" />
            </div>
            <p className="text-[10px] text-slate-500 mt-2">
              Ao trocar de canal, comissão, taxa e imposto são atualizados com o preset do canal.
            </p>
          </Section>

          {/* Other percentages */}
          <Section title="Custos indiretos" icon={<Percent className="w-3 h-3" />}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <div className="flex items-center justify-between mb-1 gap-2">
                  <span className="block text-[10px] uppercase tracking-widest font-bold text-slate-600">
                    Custo de operação
                  </span>
                  <button
                    type="button"
                    onClick={() => setShowOpCostCalc(true)}
                    data-testid="pricing-op-cost-btn"
                    className="text-[9px] uppercase tracking-wider font-bold text-[#002EA6] hover:underline inline-flex items-center gap-1"
                    title="Descobrir custo operacional a partir do faturamento anual"
                  >
                    <Calculator className="w-2.5 h-2.5" /> Descobrir
                  </button>
                </div>
                <div className="relative">
                  <input
                    data-testid="pricing-custos-fixos"
                    type="number" step="0.01" min="0"
                    value={custosFixos}
                    onChange={(e) => setCustosFixos(e.target.value)}
                    className="w-full border border-slate-300 bg-white px-3 py-2 pr-9 text-sm font-mono rounded-sm focus:outline-none focus:border-[#002EA6]"
                  />
                  <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400 pointer-events-none">%</span>
                </div>
              </div>
              <NumField label="Marketing / anúncios" value={marketing} onChange={setMarketing} testid="pricing-marketing" suffix="%" />
            </div>
          </Section>

          {/* Margin target */}
          <Section title="Meta de lucro" icon={<Target className="w-3 h-3" />}>
            <NumField label="Margem de lucro líquida desejada" value={margem} onChange={setMargem} testid="pricing-margem" suffix="%" big />
          </Section>
        </div>

        {/* ==================== RIGHT: STICKY RESULTS ==================== */}
        <div className="lg:sticky lg:top-4 space-y-4">
          {invalid ? (
            <div className="border-2 border-red-300 bg-red-50 rounded-xl p-6 flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-red-700 flex-shrink-0" />
              <div>
                <div className="text-sm font-black text-red-900">Soma inviável</div>
                <div className="text-xs text-red-800 mt-1">
                  A soma de comissão + impostos + custo de operação + marketing + margem chegou a {pct(somaPct)}.
                  Reduza algum percentual (o total precisa ficar abaixo de 100%).
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Hero price card */}
              <div
                className="rounded-xl p-6 text-white relative overflow-hidden"
                style={{ background: "linear-gradient(135deg, #002EA6 0%, #001a5e 100%)" }}
                data-testid="pricing-result"
              >
                <div className="text-[10px] uppercase tracking-widest font-black text-blue-200">Preço de venda sugerido</div>
                <div className="text-5xl font-black tracking-tighter mt-1" data-testid="pricing-preco-venda">
                  {brl(precoVenda)}
                </div>
                <div className="grid grid-cols-3 gap-3 mt-5 pt-4 border-t border-white/20">
                  <div>
                    <div className="text-[9px] uppercase tracking-widest font-bold text-blue-200">Markup</div>
                    <div className="text-lg font-black tracking-tight mt-0.5">{markup.toFixed(2)}×</div>
                  </div>
                  <div>
                    <div className="text-[9px] uppercase tracking-widest font-bold text-blue-200">Lucro líquido</div>
                    <div className="text-lg font-black tracking-tight mt-0.5 text-emerald-300">{brl(lucroLiquido)}</div>
                  </div>
                  <div>
                    <div className="text-[9px] uppercase tracking-widest font-bold text-blue-200">Preço mínimo</div>
                    <div className="text-lg font-black tracking-tight mt-0.5">{brl(precoMinimo)}</div>
                  </div>
                </div>
              </div>

              {/* Breakdown */}
              <div className="border border-slate-200 bg-white rounded-xl p-4">
                <h3 className="text-[10px] uppercase tracking-widest font-black text-slate-700 border-b border-slate-100 pb-2 mb-3">
                  Composição do preço
                </h3>
                <div className="space-y-2.5">
                  {breakdown.map((b) => {
                    const perc = precoVenda > 0 ? (b.value / precoVenda) * 100 : 0;
                    return (
                      <div key={b.label}>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="font-bold text-slate-700 flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full" style={{ background: b.color }} />
                            {b.label}
                          </span>
                          <span className="font-mono text-slate-600">
                            {brl(b.value)} <span className="text-slate-400">· {pct(perc)}</span>
                          </span>
                        </div>
                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full transition-all" style={{ width: `${Math.min(100, perc)}%`, background: b.color }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Test a specific price */}
              <div className="border border-slate-200 bg-white rounded-xl p-4">
                <h3 className="text-[10px] uppercase tracking-widest font-black text-slate-700 border-b border-slate-100 pb-2 mb-3 flex items-center gap-1.5">
                  <TrendingUp className="w-3 h-3" /> Testar um preço específico
                </h3>
                <NumField
                  label="Preço que você quer praticar"
                  value={precoTeste}
                  onChange={setPrecoTeste}
                  testid="pricing-preco-teste"
                  prefix="R$"
                />
                {testeAtivo && (
                  <div className={`border-2 rounded-lg p-4 mt-3 ${
                    margemTeste < 0 ? "border-red-300 bg-red-50" :
                    margemTeste < n(margem) ? "border-amber-300 bg-amber-50" :
                    "border-emerald-300 bg-emerald-50"
                  }`}>
                    <div className="grid grid-cols-3 gap-3">
                      <MiniStat label="Preço testado" value={brl(n(precoTeste))} />
                      <MiniStat label="Lucro" value={brl(lucroTeste)} accent={lucroTeste >= 0 ? "emerald" : "red"} />
                      <MiniStat label="Margem real" value={pct(margemTeste)} accent={
                        margemTeste < 0 ? "red" : margemTeste < n(margem) ? "amber" : "emerald"
                      } />
                    </div>
                    {margemTeste < 0 && (
                      <div className="mt-3 text-xs text-red-800 font-bold">
                        ⚠ Este preço gera <b>prejuízo</b> de {brl(Math.abs(lucroTeste))} por unidade.
                      </div>
                    )}
                    {margemTeste >= 0 && margemTeste < n(margem) && (
                      <div className="mt-3 text-xs text-amber-900">
                        Preço testado abaixo da meta. Diferença de <b>{pct(n(margem) - margemTeste)}</b> na margem.
                        Para atingir sua meta de {pct(n(margem))}, pratique <b>{brl(precoVenda)}</b>.
                      </div>
                    )}
                    {margemTeste >= n(margem) && (
                      <div className="mt-3 text-xs text-emerald-900 font-bold">
                        ✓ Preço acima da meta! {pct(margemTeste - n(margem))} a mais do que o alvo.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {showConfig && (
        <ChannelsConfigModal
          onClose={() => setShowConfig(false)}
          onSaved={() => { loadCanais(); }}
        />
      )}

      {showOpCostCalc && (
        <OperationalCostModal
          onClose={() => setShowOpCostCalc(false)}
          onApply={(percentage) => {
            setCustosFixos(String(percentage.toFixed(2)));
            setShowOpCostCalc(false);
            toast.success(`Custo de operação preenchido: ${pct(percentage)}`);
          }}
        />
      )}
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <div className="border border-slate-200 bg-white rounded-xl p-4 space-y-3">
      <h3 className="text-[10px] uppercase tracking-widest font-black text-slate-700 border-b border-slate-100 pb-2 flex items-center gap-1.5">
        {icon}
        {title}
      </h3>
      {children}
    </div>
  );
}

function NumField({ label, value, onChange, testid, prefix, suffix, big }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-widest font-bold text-slate-600 mb-1">{label}</span>
      <div className="relative">
        {prefix && (
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400 pointer-events-none">
            {prefix}
          </span>
        )}
        <input
          data-testid={testid}
          type="number"
          step="0.01"
          min="0"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`w-full border border-slate-300 bg-white text-sm font-mono rounded-sm focus:outline-none focus:border-[#002EA6] ${
            big ? "py-2.5 text-base font-bold" : "py-2"
          } ${prefix ? "pl-9" : "pl-3"} ${suffix ? "pr-9" : "pr-3"}`}
        />
        {suffix && (
          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400 pointer-events-none">
            {suffix}
          </span>
        )}
      </div>
    </label>
  );
}

function MiniStat({ label, value, accent = "slate" }) {
  const colors = {
    slate: "text-slate-700",
    emerald: "text-emerald-700",
    red: "text-red-700",
    amber: "text-amber-700",
  };
  return (
    <div>
      <div className="text-[9px] uppercase tracking-widest font-bold text-slate-500">{label}</div>
      <div className={`text-base font-black tracking-tight mt-0.5 ${colors[accent]}`}>{value}</div>
    </div>
  );
}

function ProductAutocomplete({ onSelect }) {
  // Scopes the search + realtime-cost lookup to the active Bling so
  // Pricing screens for Loja A and Loja B never mix products or their
  // costs. Legacy accounts without a connected Bling still see the
  // unfiltered results (the backend just doesn't apply the filter).
  const { activeBling } = useActiveBling();
  const activeBlingId = activeBling?.id || "";
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [selected, setSelected] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [stale, setStale] = useState(false);
  const [debugKeys, setDebugKeys] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    if (!q.trim()) { setResults([]); setNotFound(false); return; }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ q });
        if (activeBlingId) params.append("integration_id", activeBlingId);
        const { data } = await api.get(`/bling/catalog/search?${params.toString()}`);
        setResults(data.items || []);
        setNotFound((data.items || []).length === 0);
      } catch (_e) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => timer.current && clearTimeout(timer.current);
  }, [q, activeBlingId]);

  const pick = async (p) => {
    // Show the cached item immediately so the UI feels snappy…
    // Coerce preco_custo defensively — Mongo docs from legacy syncs
    // may store this as null, which the backend serializes as null.
    // If we passed null down to setCustoProduto's onSelect guard it
    // would early-return and leave the "Custo do Produto" field
    // blank even though the search result IS the correct product.
    const initial = {
      ...p,
      preco_custo: p.preco_custo == null ? 0 : Number(p.preco_custo),
      preco: p.preco == null ? 0 : Number(p.preco),
    };
    console.log("[Pricing] product picked:", initial);
    setSelected(initial);
    setResults([]);
    setQ("");
    setStale(false);
    setDebugKeys(null);
    onSelect(initial);

    // …then hit Bling in real-time to guarantee the cost is fresh.
    setRefreshing(true);
    try {
      const url = activeBlingId
        ? `/bling/catalog/product/${p.id}/cost-realtime?integration_id=${encodeURIComponent(activeBlingId)}`
        : `/bling/catalog/product/${p.id}/cost-realtime`;
      const { data } = await api.get(url);
      console.log("[Pricing] cost-realtime response:", data);
      const fresh = {
        ...initial,
        preco_custo: data.preco_custo == null ? 0 : Number(data.preco_custo),
        preco: data.preco == null ? 0 : Number(data.preco),
        imagem_url: data.imagem_url || initial.imagem_url,
        nome: data.nome || initial.nome,
        codigo: data.codigo || initial.codigo,
      };
      setSelected(fresh);
      setStale(!!data.stale);
      setDebugKeys(data._debug_bling_keys || null);
      onSelect(fresh);
    } catch (_e) {
      setStale(true);
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="relative">
      <label className="block">
        <span className="block text-[10px] uppercase tracking-widest font-bold text-slate-600 mb-1">
          Buscar produto no catálogo (opcional)
        </span>
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
          <input
            data-testid="pricing-product-search"
            type="text"
            placeholder="Nome ou SKU..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full border border-slate-300 bg-white pl-10 pr-3 py-2 text-sm rounded-sm focus:outline-none focus:border-[#002EA6]"
          />
          {loading && <Loader2 className="w-4 h-4 text-slate-400 animate-spin absolute right-3 top-2.5" />}
        </div>
      </label>

      {results.length > 0 && (
        <div className="absolute z-10 mt-1 w-full max-h-64 overflow-auto bg-white border border-slate-300 rounded-sm shadow-lg" data-testid="pricing-search-results">
          {results.map((p) => (
            <button
              key={p.id}
              onClick={() => pick(p)}
              className="w-full px-3 py-2 text-left hover:bg-slate-50 border-b border-slate-100 flex items-center gap-2"
            >
              {p.imagem_url ? (
                <img src={p.imagem_url} alt="" className="w-8 h-8 object-cover rounded" />
              ) : (
                <div className="w-8 h-8 bg-slate-100 rounded" />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold truncate">{p.nome}</div>
                <div className="text-xs text-slate-500 font-mono">{p.codigo || "sem SKU"} · {brl(p.preco_custo)}</div>
              </div>
            </button>
          ))}
        </div>
      )}

      {q && notFound && !loading && (
        <div className="mt-1 text-xs text-amber-800 bg-amber-50 border border-amber-200 px-3 py-2 rounded-sm">
          Nenhum produto encontrado. Preencha o custo manualmente abaixo.
        </div>
      )}

      {selected && (
        <div
          data-testid="pricing-selected-product"
          className={`mt-2 flex items-center gap-3 border-2 px-3 py-2.5 rounded-md ${
            stale
              ? "border-amber-200 bg-amber-50"
              : "border-emerald-200 bg-emerald-50"
          }`}
        >
          {/* Product image (or placeholder) */}
          <div className="flex-shrink-0 relative">
            {selected.imagem_url ? (
              <img
                data-testid="pricing-selected-image"
                src={selected.imagem_url}
                alt={selected.nome}
                className="w-14 h-14 object-cover rounded border border-slate-200 bg-white"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            ) : (
              <div className="w-14 h-14 bg-white border border-slate-200 rounded flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-slate-300" />
              </div>
            )}
            {refreshing && (
              <div className="absolute inset-0 bg-white/70 rounded flex items-center justify-center">
                <Loader2 className="w-4 h-4 animate-spin text-slate-600" />
              </div>
            )}
          </div>

          {/* Product info */}
          <div className="flex-1 min-w-0 text-xs">
            <div className="font-bold text-slate-900 truncate" title={selected.nome}>
              {selected.nome}
            </div>
            {selected.codigo && (
              <div className="font-mono text-[10px] text-slate-500 mt-0.5">SKU {selected.codigo}</div>
            )}
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              <span className="text-slate-600">Custo:</span>
              {refreshing ? (
                <span className="text-slate-500">atualizando…</span>
              ) : (
                <span
                  data-testid="pricing-realtime-cost"
                  className={`font-mono font-bold ${
                    Number(selected.preco_custo || 0) === 0 ? "text-red-700" : "text-slate-900"
                  }`}
                >
                  {brl(selected.preco_custo)}
                </span>
              )}
              {!refreshing && Number(selected.preco_custo || 0) === 0 && !stale && (
                <span className="text-[10px] font-bold uppercase tracking-wider text-red-800 bg-red-100 px-1.5 py-0.5 rounded-sm">
                  custo não cadastrado no Bling
                </span>
              )}
              {!refreshing && Number(selected.preco_custo || 0) > 0 && (stale ? (
                <span className="text-[10px] font-bold uppercase tracking-wider text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded-sm">
                  cache — Bling indisponível
                </span>
              ) : (
                <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-800 bg-emerald-100 px-1.5 py-0.5 rounded-sm">
                  tempo real
                </span>
              ))}
              {!refreshing && Number(selected.preco_custo || 0) === 0 && stale && (
                <span className="text-[10px] font-bold uppercase tracking-wider text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded-sm">
                  cache — Bling indisponível
                </span>
              )}
            </div>
          </div>

          <button
            onClick={() => setSelected(null)}
            className="text-slate-400 hover:text-slate-900 flex-shrink-0 self-start p-1"
            title="Remover seleção"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Diagnostic panel: only shown when Bling returned a product but we
          couldn't extract a cost. Helps the user identify the Bling field
          path so we can add it to `_extract_bling_cost` on the backend. */}
      {selected && !refreshing && Number(selected.preco_custo || 0) === 0 && debugKeys && (
        <details
          data-testid="pricing-cost-debug"
          className="mt-2 border border-amber-200 bg-amber-50 rounded-sm px-3 py-2 text-xs"
        >
          <summary className="cursor-pointer font-bold text-amber-900 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" />
            Custo veio como zero — clique para diagnóstico
          </summary>
          <div className="mt-2 space-y-2 text-amber-900">
            <p>
              O Bling retornou o produto mas não achamos um campo de custo. Isso normalmente é <b>escopo</b> —
              o app cadastrado no Bling precisa da permissão <b>“Produtos”</b> com leitura de custos, ou o produto
              não tem custo cadastrado. Reconecte o Bling após ativar o escopo.
            </p>
            <p className="text-[10px]">
              <b>Chaves top-level retornadas:</b>
            </p>
            <code className="block bg-white/70 border border-amber-200 rounded p-2 font-mono text-[10px] overflow-auto max-h-32">
              {(debugKeys.top_level || []).join(", ") || "(nenhuma)"}
            </code>
            {debugKeys.nested && Object.keys(debugKeys.nested).length > 0 && (
              <>
                <p className="text-[10px] pt-1"><b>Sub-objetos:</b></p>
                <code className="block bg-white/70 border border-amber-200 rounded p-2 font-mono text-[10px] overflow-auto max-h-32">
                  {Object.entries(debugKeys.nested).map(([k, arr]) => (
                    `${k}: [${(arr || []).join(", ")}]`
                  )).join("\n")}
                </code>
              </>
            )}
            <p className="text-[10px] pt-1 text-amber-800">
              Envie o print destas chaves ao suporte se o problema persistir.
            </p>
          </div>
        </details>
      )}
    </div>
  );
}

function ChannelsConfigModal({ onClose, onSaved }) {
  const [rows, setRows] = useState([]);       // existing channels from API
  const [drafts, setDrafts] = useState([]);   // new unsaved rows
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/pricing/channels");
      setRows(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erro ao carregar canais");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const patchRow = (id, patch) => {
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch, _dirty: true } : r)));
  };

  const removeRow = async (id) => {
    if (!confirm("Remover este canal? Esta ação não pode ser desfeita.")) return;
    try {
      await api.delete(`/pricing/channels/${id}`);
      setRows((rs) => rs.filter((r) => r.id !== id));
      toast.success("Canal removido");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erro ao remover");
    }
  };

  const addDraft = () => {
    setDrafts((ds) => [
      ...ds,
      { _tmp: Math.random().toString(36).slice(2), label: "", com: 0, taxa_fixa: 0, imposto: 0 },
    ]);
  };

  // Auto-generate a stable slug key from the label. Falls back to a
  // random suffix so uniqueness is guaranteed even if two channels have
  // similar names (e.g. two "Kwai" entries).
  const slugify = (s) =>
    (s || "")
      .toString()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "") // strip accents
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, 40);

  const patchDraft = (tmp, patch) => {
    setDrafts((ds) => ds.map((d) => (d._tmp === tmp ? { ...d, ...patch } : d)));
  };

  const removeDraft = (tmp) => {
    setDrafts((ds) => ds.filter((d) => d._tmp !== tmp));
  };

  const saveAll = async () => {
    setSaving(true);
    let ok = 0;
    let err = 0;
    try {
      // Update dirty existing rows
      for (const r of rows.filter((x) => x._dirty)) {
        try {
          await api.put(`/pricing/channels/${r.id}`, {
            key: r.key, label: r.label,
            com: Number(r.com || 0),
            taxa_fixa: Number(r.taxa_fixa || 0),
            imposto: Number(r.imposto || 0),
          });
          ok += 1;
        } catch (e) {
          err += 1;
          toast.error(`${r.label || r.key}: ${formatApiError(e.response?.data?.detail) || "erro"}`);
        }
      }
      // Create drafts — auto-generate key from label if user didn't type one
      const existingKeys = new Set(rows.map((r) => r.key));
      for (const d of drafts) {
        if (!d.label) {
          err += 1;
          toast.error(`Preencha o nome do novo canal`);
          continue;
        }
        let key = slugify(d.label);
        if (!key) key = `canal_${Math.random().toString(36).slice(2, 8)}`;
        // Ensure uniqueness against already-persisted rows
        let final = key;
        let i = 2;
        while (existingKeys.has(final)) {
          final = `${key}_${i}`;
          i += 1;
        }
        existingKeys.add(final);
        try {
          await api.post("/pricing/channels", {
            key: final, label: d.label,
            com: Number(d.com || 0),
            taxa_fixa: Number(d.taxa_fixa || 0),
            imposto: Number(d.imposto || 0),
          });
          ok += 1;
        } catch (e) {
          err += 1;
          toast.error(`${d.label}: ${formatApiError(e.response?.data?.detail) || "erro"}`);
        }
      }
      if (ok > 0) toast.success(`${ok} ${ok === 1 ? "canal salvo" : "canais salvos"}`);
      if (err === 0) {
        onSaved?.();
        onClose();
      } else {
        await load();
        setDrafts([]);
        onSaved?.();
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="channels-config-modal"
    >
      <div
        className="bg-white rounded-xl border border-slate-200 shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
              <Settings className="w-3 h-3" /> Configuração
            </div>
            <h2 className="text-xl font-black tracking-tight text-slate-900">Canais de venda</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Edite comissões e taxas ou adicione novos canais. Alterações se aplicam à calculadora.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-900 p-1"
            data-testid="channels-config-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-auto p-5">
          {loading ? (
            <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto" /></div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                  <th className="text-left py-2 pr-3">Nome</th>
                  <th className="text-right py-2 pr-3 w-28">Comissão (%)</th>
                  <th className="text-right py-2 pr-3 w-32">Taxa fixa (R$)</th>
                  <th className="text-right py-2 pr-3 w-24">Imposto (%)</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-testid="channels-config-tbody">
                {rows.map((r) => (
                  <tr key={r.id} className={r._dirty ? "bg-amber-50/40" : ""}>
                    <td className="py-2 pr-3">
                      <input
                        data-testid={`channel-label-${r.key}`}
                        type="text"
                        value={r.label}
                        onChange={(e) => patchRow(r.id, { label: e.target.value })}
                        className="w-full border border-slate-300 bg-white px-2 py-1.5 text-sm rounded-sm focus:outline-none focus:border-[#002EA6]"
                      />
                    </td>
                    <td className="py-2 pr-3">
                      <input
                        data-testid={`channel-com-${r.key}`}
                        type="number" step="0.01" min="0"
                        value={r.com}
                        onChange={(e) => patchRow(r.id, { com: e.target.value })}
                        className="w-full border border-slate-300 bg-white px-2 py-1.5 text-sm font-mono text-right rounded-sm focus:outline-none focus:border-[#002EA6]"
                      />
                    </td>
                    <td className="py-2 pr-3">
                      <input
                        type="number" step="0.01" min="0"
                        value={r.taxa_fixa}
                        onChange={(e) => patchRow(r.id, { taxa_fixa: e.target.value })}
                        className="w-full border border-slate-300 bg-white px-2 py-1.5 text-sm font-mono text-right rounded-sm focus:outline-none focus:border-[#002EA6]"
                      />
                    </td>
                    <td className="py-2 pr-3">
                      <input
                        data-testid={`channel-imposto-${r.key}`}
                        type="number" step="0.01" min="0"
                        value={r.imposto ?? 0}
                        onChange={(e) => patchRow(r.id, { imposto: e.target.value })}
                        className="w-full border border-slate-300 bg-white px-2 py-1.5 text-sm font-mono text-right rounded-sm focus:outline-none focus:border-[#002EA6]"
                      />
                    </td>
                    <td className="py-2">
                      <button
                        onClick={() => removeRow(r.id)}
                        className="text-slate-400 hover:text-red-600 p-1"
                        title="Remover canal"
                        data-testid={`channel-delete-${r.key}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
                {drafts.map((d) => (
                  <tr key={d._tmp} className="bg-emerald-50/40">
                    <td className="py-2 pr-3">
                      <input
                        data-testid="channel-new-label"
                        type="text" placeholder="Nome do canal"
                        value={d.label}
                        onChange={(e) => patchDraft(d._tmp, { label: e.target.value })}
                        className="w-full border border-slate-300 bg-white px-2 py-1.5 text-sm rounded-sm focus:outline-none focus:border-[#002EA6]"
                      />
                    </td>
                    <td className="py-2 pr-3">
                      <input
                        type="number" step="0.01" min="0"
                        value={d.com}
                        onChange={(e) => patchDraft(d._tmp, { com: e.target.value })}
                        className="w-full border border-slate-300 bg-white px-2 py-1.5 text-sm font-mono text-right rounded-sm focus:outline-none focus:border-[#002EA6]"
                      />
                    </td>
                    <td className="py-2 pr-3">
                      <input
                        type="number" step="0.01" min="0"
                        value={d.taxa_fixa}
                        onChange={(e) => patchDraft(d._tmp, { taxa_fixa: e.target.value })}
                        className="w-full border border-slate-300 bg-white px-2 py-1.5 text-sm font-mono text-right rounded-sm focus:outline-none focus:border-[#002EA6]"
                      />
                    </td>
                    <td className="py-2 pr-3">
                      <input
                        data-testid="channel-new-imposto"
                        type="number" step="0.01" min="0"
                        value={d.imposto ?? 0}
                        onChange={(e) => patchDraft(d._tmp, { imposto: e.target.value })}
                        className="w-full border border-slate-300 bg-white px-2 py-1.5 text-sm font-mono text-right rounded-sm focus:outline-none focus:border-[#002EA6]"
                      />
                    </td>
                    <td className="py-2">
                      <button
                        onClick={() => removeDraft(d._tmp)}
                        className="text-slate-400 hover:text-red-600 p-1"
                        title="Descartar"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <button
            onClick={addDraft}
            disabled={loading}
            className="mt-4 px-3 py-2 border-2 border-dashed border-slate-300 hover:border-[#002EA6] hover:bg-slate-50 text-slate-700 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5 rounded-sm w-full justify-center"
            data-testid="channels-add-btn"
          >
            <Plus className="w-3.5 h-3.5" /> Adicionar canal
          </button>
        </div>

        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
          <span className="text-xs text-slate-500">
            {rows.filter((r) => r._dirty).length + drafts.length} alteração(ões) pendente(s)
          </span>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-slate-700 hover:bg-slate-100 rounded-sm"
            >
              Cancelar
            </button>
            <button
              onClick={saveAll}
              disabled={saving || (rows.filter((r) => r._dirty).length === 0 && drafts.length === 0)}
              data-testid="channels-save-btn"
              className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-white inline-flex items-center gap-2 disabled:opacity-60 rounded-sm"
              style={{ background: "#002EA6" }}
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
              Salvar alterações
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


function OperationalCostModal({ onClose, onApply }) {
  const [faturamentoAnual, setFaturamentoAnual] = useState("");
  const [custoMensal, setCustoMensal] = useState("");
  const [resultado, setResultado] = useState(null);

  const n = (v) => Number(String(v).replace(",", ".") || 0) || 0;

  const calcular = () => {
    const fatAnual = n(faturamentoAnual);
    const custo = n(custoMensal);
    if (fatAnual <= 0) {
      toast.error("Informe o faturamento anual");
      return;
    }
    if (custo <= 0) {
      toast.error("Informe o custo mensal da operação");
      return;
    }
    const mediaMensal = fatAnual / 12;
    const percentual = (custo / mediaMensal) * 100;
    setResultado({ mediaMensal, percentual });
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="op-cost-modal"
    >
      <div
        className="bg-white rounded-xl border border-slate-200 shadow-2xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
              <Calculator className="w-3 h-3" /> Descobridor
            </div>
            <h2 className="text-xl font-black tracking-tight text-slate-900">Custo operacional</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Calcule que percentual do seu faturamento é consumido pela operação.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-900 p-1"
            data-testid="op-cost-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <label className="block">
            <span className="block text-[10px] uppercase tracking-widest font-bold text-slate-600 mb-1">
              Faturamento anual (último ano)
            </span>
            <div className="relative">
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400 pointer-events-none">R$</span>
              <input
                data-testid="op-cost-faturamento-anual"
                type="number" step="0.01" min="0"
                placeholder="Ex: 1200000"
                value={faturamentoAnual}
                onChange={(e) => { setFaturamentoAnual(e.target.value); setResultado(null); }}
                className="w-full border border-slate-300 bg-white pl-9 pr-3 py-2.5 text-sm font-mono rounded-sm focus:outline-none focus:border-[#002EA6]"
              />
            </div>
          </label>

          <label className="block">
            <span className="block text-[10px] uppercase tracking-widest font-bold text-slate-600 mb-1">
              Custo mensal da operação (tudo — aluguel, salários, sistemas, etc.)
            </span>
            <div className="relative">
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400 pointer-events-none">R$</span>
              <input
                data-testid="op-cost-custo-mensal"
                type="number" step="0.01" min="0"
                placeholder="Ex: 25000"
                value={custoMensal}
                onChange={(e) => { setCustoMensal(e.target.value); setResultado(null); }}
                className="w-full border border-slate-300 bg-white pl-9 pr-3 py-2.5 text-sm font-mono rounded-sm focus:outline-none focus:border-[#002EA6]"
              />
            </div>
          </label>

          <button
            onClick={calcular}
            data-testid="op-cost-calc-btn"
            className="w-full px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-white inline-flex items-center justify-center gap-2 rounded-sm"
            style={{ background: "#002EA6" }}
          >
            <Calculator className="w-3.5 h-3.5" /> Calcular
          </button>

          {resultado && (
            <div className="border-2 border-emerald-200 bg-emerald-50 rounded-lg p-4 space-y-2" data-testid="op-cost-result">
              <div className="flex justify-between text-xs">
                <span className="text-slate-700">Faturamento médio mensal</span>
                <span className="font-mono font-bold text-slate-900">{brl(resultado.mediaMensal)}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-700">Custo mensal da operação</span>
                <span className="font-mono font-bold text-slate-900">{brl(n(custoMensal))}</span>
              </div>
              <div className="border-t border-emerald-200 pt-2 flex justify-between items-baseline">
                <span className="text-xs uppercase tracking-widest font-black text-emerald-700">Custo de operação</span>
                <span className="font-mono font-black text-2xl text-emerald-900" data-testid="op-cost-percent">
                  {pct(resultado.percentual)}
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-slate-700 hover:bg-slate-100 rounded-sm"
          >
            Cancelar
          </button>
          <button
            onClick={() => resultado && onApply(resultado.percentual)}
            disabled={!resultado}
            data-testid="op-cost-apply-btn"
            className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-white disabled:opacity-40 rounded-sm"
            style={{ background: "#002EA6" }}
          >
            Aplicar na precificação
          </button>
        </div>
      </div>
    </div>
  );
}

